/**
 * Products Domain - English Translations
 * Flat structure (no nested 'products' wrapper) - namespace added by portal
 */

export const enTranslations = {
  title: 'Products',
  subtitle: 'Manage SKUs, variants, and categories',
  // Unit labels (flat keys for UI components)
  packaging: {
    bags: 'Bags',
    bulk: 'Bulk',
  },
  bag: 'Bag',
  bags: 'Bags',
  bulk: 'Bulk',
  ton: 'Ton',
  tons: 'Tons',
  perBag: 'Per Bag',
  perTon: 'Per Ton',
  addItem: 'Add Item',
  addSku: 'Add SKU',
  editSku: 'Edit SKU',
  addVariant: 'Add Variant',
  editVariant: 'Edit Variant',
  addCategory: 'Add Category',
  editCategory: 'Edit Category',
  skuDetails: 'SKU Details',
  variantDetails: 'Variant Details',
  noSkus: 'No SKUs found',
  noVariants: 'No variants found',
  noCategories: 'No categories found',
  searchPlaceholder: 'Search products...',
  overview: 'Overview',

  // Timeline
  timelineTab: 'Timeline',
  timelineEmpty: 'No events yet',
  timelineEmptyDesc: 'Changes to this variant, its SKU, and categories will appear here',
  selectVariant: 'Select variant',

  // Stats
  stats: {
    totalSkus: 'Total SKUs',
    activeSkus: 'Active SKUs',
    totalVariants: 'Total Variants',
    activeVariants: 'Active Variants',
    totalCategories: 'Categories',
  },

  // Form fields
  form: {
    name: 'Name',
    nameEn: 'Name (English)',
    nameAr: 'Name (Arabic)',
    description: 'Description',
    descriptionEn: 'Description (English)',
    descriptionAr: 'Description (Arabic)',
    categories: 'Categories',
    selectCategories: 'Select categories',
    sku: 'SKU',
    selectSku: 'Select SKU',
    variantCode: 'Variant Code',
    variantCodePlaceholder: 'e.g. VAR-001',
    barcode: 'Barcode',
    weight: 'Weight (kg)',
    packaging: 'Packaging Type',
    portalAvailability: 'Available In',
    deliveryGrouping: 'Delivery Grouping Rule',
    isActive: 'Active',
    sapIntegration: 'SAP Integration',
    sapMaterialCode: 'SAP Material Code',
    sapMaterialCodePlaceholder: 'e.g. 000000000050000010',
    sapStorageLocationCode: 'SAP Storage Location Code',
    sapStorageLocationCodePlaceholder: 'e.g. Z017',
    sapDescription: 'SAP Description',
    sapBaseUnit: 'SAP Base Unit',
    code: 'Code',
    codePlaceholder: 'e.g. CAT-001',
    color: 'Color',
    parentCategory: 'Parent Category',
    noParent: 'No parent (top-level)',
  },

  portalAvailability: {
    ecommerce: 'Ecommerce',
    portal: 'Portal',
    both: 'Both',
  },

  // Packaging types (nested for form select options)
  packagingTypes: {
    bags: 'Bags',
    bulk: 'Bulk',
  },

  // Delivery grouping rules
  deliveryGrouping: {
    allowMixed: 'Allow Mixed',
    allowMixedDesc: 'Can be delivered with other packaging types',
    requireSame: 'Require Same',
    requireSameDesc: 'Must be delivered with same packaging type only',
  },

  // Validation messages
  validation: {
    nameRequired: 'Name is required',
    codeRequired: 'Variant code is required',
    barcodeRequired: 'Barcode is required',
    weightRequired: 'Weight is required',
    weightMin: 'Weight must be at least 1 kg',
    skuRequired: 'SKU is required',
    packagingRequired: 'Packaging type is required',
  },

  // Delete confirmations
  delete: {
    confirmSku: 'Are you sure you want to delete this SKU?',
    confirmVariant: 'Are you sure you want to delete this variant?',
    warning: 'This action cannot be undone. All associated variants will also be removed.',
    variantWarning:
      'This action cannot be undone. The variant will be removed from all availability matrices.',
  },

  // Success messages
  success: {
    skuCreated: 'SKU created successfully',
    skuUpdated: 'SKU updated successfully',
    skuDeleted: 'SKU deleted successfully',
    variantCreated: 'Variant created successfully',
    variantUpdated: 'Variant updated successfully',
    variantDeleted: 'Variant deleted successfully',
    categoryCreated: 'Category created successfully',
    categoryUpdated: 'Category updated successfully',
  },

  // Error messages
  error: {
    fetchFailed: 'Failed to fetch products',
    createFailed: 'Failed to create product',
    updateFailed: 'Failed to update product',
    deleteFailed: 'Failed to delete product',
  },

  // Filters
  filter: {
    all: 'All',
    active: 'Active',
    inactive: 'Inactive',
    allPackaging: 'All Packaging',
    allCategories: 'All Categories',
  },

  // Certificates section
  certificates: {
    title: 'Certificates',
    refresh: 'Refresh',
    addCertificate: 'Add Certificate',
    searchPlaceholder: 'Search certificates...',
    typeFilter: 'Type',
    allTypes: 'All Types',
    typeGeneral: 'General',
    typeVariant: 'Variant',
    allStatuses: 'All Status',
    statusActive: 'Active',
    statusInactive: 'Inactive',
    colTitle: 'Title',
    colType: 'Type',
    colLinkedVariant: 'Linked Variant',
    colFile: 'File',
    colStatus: 'Status',
    colActions: 'Actions',
    noCertificates: 'No certificates found',
    addFirstCertificate: 'Add your first certificate',
    typeGeneralLabel: 'General',
    typeVariantLabel: 'Variant',
    typeUnlinkedLabel: 'Unlinked',
    noFile: 'No file',
    deleteConfirm: 'Delete "$1"?',
    // Form
    editCertificate: 'Edit Certificate',
    fieldTitle: 'Title',
    titlePlaceholder: 'e.g. ISO 9001:2015',
    titleRequired: 'Title is required',
    fieldDescription: 'Description',
    descriptionPlaceholder: 'Optional description',
    fieldGeneralCert: 'General Certificate',
    generalCertHint: 'Shown on the About page',
    fieldActive: 'Active',
    activeHint: 'Visible to customers',
    fieldLinkedVariant: 'Linked Variant',
    selectVariantOptional: 'Select variant (optional)',
    fieldPdf: 'Certificate PDF',
    uploadPdf: 'Click to upload PDF',
    onlyPdfAccepted: 'Only PDF files are accepted',
    uploadFailed: 'File upload failed. Please try again.',
    cancel: 'Cancel',
    saveChanges: 'Save Changes',
    createCertificate: 'Create Certificate',
  },

  // Table headers
  table: {
    name: 'Name',
    code: 'Code',
    barcode: 'Barcode',
    weight: 'Weight',
    packaging: 'Packaging',
    status: 'Status',
    variants: 'Variants',
    categories: 'Categories',
    actions: 'Actions',
    color: 'Color',
    parent: 'Parent',
    skus: 'SKUs',
  },
}

export type ProductsTranslations = typeof enTranslations
